import React from 'react';
import { Chip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Send as SentIcon,
  Cancel as CancelledIcon,
  Schedule as ScheduledIcon,
} from '@mui/icons-material';

export type StatusType =
  | 'pending'
  | 'sent'
  | 'delivered'
  | 'failed'
  | 'cancelled'
  | 'scheduled'
  | 'active'
  | 'inactive'
  | 'upcoming'
  | 'due'
  | 'overdue'
  | 'completed';

interface StatusBadgeProps {
  status: StatusType;
  size?: 'small' | 'medium';
}

const statusConfig: Record<
  StatusType,
  {
    label: string;
    color: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
    icon?: React.ReactElement;
  }
> = {
  pending: {
    label: 'Pending',
    color: 'warning',
    icon: <PendingIcon />,
  },
  sent: {
    label: 'Sent',
    color: 'info',
    icon: <SentIcon />,
  },
  delivered: {
    label: 'Delivered',
    color: 'success',
    icon: <CheckCircleIcon />,
  },
  failed: {
    label: 'Failed',
    color: 'error',
    icon: <ErrorIcon />,
  },
  cancelled: {
    label: 'Cancelled',
    color: 'default',
    icon: <CancelledIcon />,
  },
  scheduled: {
    label: 'Scheduled',
    color: 'info',
    icon: <ScheduledIcon />,
  },
  active: {
    label: 'Active',
    color: 'success',
    icon: <CheckCircleIcon />,
  },
  inactive: {
    label: 'Inactive',
    color: 'default',
    icon: <CancelledIcon />,
  },
  upcoming: {
    label: 'Upcoming',
    color: 'info',
    icon: <ScheduledIcon />,
  },
  due: {
    label: 'Due',
    color: 'warning',
    icon: <PendingIcon />,
  },
  overdue: {
    label: 'Overdue',
    color: 'error',
    icon: <ErrorIcon />,
  },
  completed: {
    label: 'Completed',
    color: 'success',
    icon: <CheckCircleIcon />,
  },
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'small' }) => {
  const config = statusConfig[status] || statusConfig.pending;

  return (
    <Chip
      label={config.label}
      color={config.color}
      size={size}
      icon={config.icon}
      sx={{
        fontWeight: 500,
        fontSize: size === 'small' ? '0.75rem' : '0.875rem',
      }}
    />
  );
};
